import React, { useState } from "react";
import { Link } from "react-router-dom";
import { 
  Phone, Instagram, Facebook, X,
  TrendingUp, Wallet, PiggyBank, Target, BarChart3, Shield,
  Globe, Building, Coins, ChevronDown, Check,
  LineChart, DollarSign, Home as HomeIcon
} from "lucide-react";
import usePageMeta from "./hooks/usePageMeta";

// Stripe Payment Link - Passo 1 da integração (apenas redirect)
const STRIPE_CHECKOUT_URL = "https://buy.stripe.com/4gMdR2biF5bAd5jgSA4ko00";

function LandingAP() {
  usePageMeta("AP - Finanças Pessoais", "ap");
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  // Handler para redirecionar ao Stripe Checkout
  const handleAssinarAgora = () => {
    window.location.href = STRIPE_CHECKOUT_URL;
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqItems = [
    {
      question: "Como funciona a assinatura?",
      answer: "A assinatura é mensal, com cobrança automática enquanto você estiver ativo."
    },
    {
      question: "Posso cancelar quando quiser?",
      answer: "Sim. O cancelamento pode ser feito a qualquer momento."
    },
    {
      question: "O pagamento é mensal?",
      answer: "Sim. A cobrança ocorre mensalmente no cartão."
    },
    {
      question: "Funciona no celular?",
      answer: "Sim. O sistema é totalmente responsivo."
    },
    {
      question: "Consigo acompanhar investimentos no exterior?",
      answer: "Sim. Empresas, REITs e renda fixa internacional."
    },
    {
      question: "O sistema mostra minha renda passiva mensal?",
      answer: "Sim. Esse é um dos principais focos do sistema."
    },
    {
      question: "Meus dados estão seguros?",
      answer: "Sim. Seus dados são tratados com segurança."
    },
    {
      question: "Preciso instalar algo?",
      answer: "Não. O sistema funciona direto no navegador."
    }
  ];

  const investmentCards = [
    { icon: TrendingUp, title: "Ações e FIIs", desc: "Bluechips e Smallcaps" },
    { icon: Globe, title: "Empresas no Exterior", desc: "Business internacionais" },
    { icon: Building, title: "REITs", desc: "Imóveis no exterior" },
    { icon: Coins, title: "Renda Fixa", desc: "Títulos e CDBs" },
    { icon: PiggyBank, title: "Poupança", desc: "Controle completo" },
    { icon: Shield, title: "Tesouro Direto", desc: "Títulos públicos" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar - Mesmo da Home */}
      <nav className="fixed top-0 w-full bg-white shadow-md z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <div className="flex-shrink-0">
              <Link to="/">
                <img 
                  src="https://customer-assets.emergentagent.com/job_finance-dash-68/artifacts/7w39hvkz_Logo%20PP%20sem%20fundo.png" 
                  alt="Pavan e Parazzi" 
                  className="h-16 w-auto"
                />
              </Link>
            </div>
            
            {/* Menu */}
            <div className="hidden md:flex space-x-4">
              <Link 
                to="/login" 
                className="px-6 py-2 rounded-lg text-white font-medium transition-all duration-300 hover:scale-105 shadow-md"
                style={{ backgroundColor: '#004C27' }}
              >
                AP – Finanças Pessoais
              </Link>
              <button 
                className="px-6 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all duration-300"
              >
                Flavor
              </button>
              <Link 
                to="/igreen"
                className="px-6 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all duration-300"
              >
                Igreen
              </Link>
              <button 
                className="px-6 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all duration-300"
              >
                SAG
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-700 hover:text-gray-900 p-2"
              >
                {mobileMenuOpen ? <X size={28} /> : (
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 py-4 px-4 space-y-3">
            <Link 
              to="/login" 
              className="block px-6 py-3 rounded-lg text-white font-medium text-center"
              style={{ backgroundColor: '#004C27' }}
            >
              AP – Finanças Pessoais
            </Link>
            <button className="block w-full px-6 py-3 rounded-lg bg-gray-100 text-gray-700 font-medium">
              Flavor
            </button>
            <Link to="/igreen" className="block px-6 py-3 rounded-lg bg-gray-100 text-gray-700 font-medium text-center">
              Igreen
            </Link>
            <button className="block w-full px-6 py-3 rounded-lg bg-gray-100 text-gray-700 font-medium">
              SAG
            </button>
          </div>
        )}
      </nav>

      {/* FAIXA 1 - HERO */}
      <section className="pt-28 pb-20 bg-gradient-to-br from-[#004C27] to-[#006437]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Texto */}
            <div className="text-white">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                Organize sua vida financeira e construa sua renda passiva
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
                Controle gastos, investimentos e acompanhe seus proventos mensais em um único sistema.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={handleAssinarAgora}
                  className="px-8 py-4 bg-white text-[#004C27] rounded-xl font-bold text-lg hover:bg-gray-100 transition-all duration-300 hover:scale-105 shadow-xl text-center cursor-pointer"
                  data-testid="hero-assinar-btn"
                >
                  Assinar agora
                </button>
                <Link 
                  to="/login"
                  className="px-8 py-4 border-2 border-white text-white rounded-xl font-semibold text-lg hover:bg-white/10 transition-all duration-300 text-center"
                >
                  Conhecer o sistema
                </Link>
              </div>
            </div>
            
            {/* Imagem/Video Placeholder */}
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 shadow-2xl">
                <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl overflow-hidden aspect-video relative">
                  {/* Mockup do Dashboard */}
                  <div className="absolute inset-0 p-4">
                    <div className="h-full bg-white rounded-lg shadow-inner p-4">
                      {/* Header mockup */}
                      <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-[#004C27] rounded-lg flex items-center justify-center">
                            <TrendingUp className="w-6 h-6 text-white" />
                          </div>
                          <span className="font-bold text-[#004C27]">Dashboard Geral</span>
                        </div>
                      </div>
                      {/* Cards mockup */}
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="bg-gradient-to-br from-[#004C27] to-[#006437] rounded-lg p-3 text-white">
                          <p className="text-xs opacity-80">Total Investido</p>
                          <p className="text-lg font-bold">R$ 56.970,00</p>
                        </div>
                        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-3 text-white">
                          <p className="text-xs opacity-80">Patrimônio</p>
                          <p className="text-lg font-bold">R$ 124.500,00</p>
                        </div>
                        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg p-3 text-white">
                          <p className="text-xs opacity-80">Dólares</p>
                          <p className="text-lg font-bold">$ 2.850,00</p>
                        </div>
                      </div>
                      {/* Chart mockup */}
                      <div className="bg-gray-50 rounded-lg p-3 h-24 flex items-end justify-around">
                        {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
                          <div key={i} className="w-6 bg-[#004C27] rounded-t" style={{height: `${h}%`}}></div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Badge */}
              <div className="absolute -bottom-4 -right-4 bg-amber-400 text-[#004C27] px-4 py-2 rounded-full font-bold text-sm shadow-lg">
                Clareza • Autoridade
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAIXA 2 - PROBLEMA (DOR) */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
                Você investe, mas não tem clareza sobre seus resultados?
              </h2>
              <ul className="space-y-4">
                {[
                  "Falta de visão consolidada dos investimentos",
                  "Dificuldade em acompanhar rendimentos mensais",
                  "Desorganização financeira entre contas e ativos",
                  "Sensação de estagnação mesmo investindo"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <X className="w-4 h-4 text-red-500" />
                    </div>
                    <span className="text-lg text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-100 rounded-xl p-4 text-center">
                    <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Dados espalhados</p>
                  </div>
                  <div className="bg-gray-100 rounded-xl p-4 text-center">
                    <LineChart className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Sem visão clara</p>
                  </div>
                  <div className="bg-gray-100 rounded-xl p-4 text-center">
                    <Wallet className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Gastos descontrolados</p>
                  </div>
                  <div className="bg-gray-100 rounded-xl p-4 text-center">
                    <Target className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Metas indefinidas</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAIXA 3 - A SOLUÇÃO */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C27] mb-6">
              Uma visão completa da sua vida financeira
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              O AP – Finanças Pessoais centraliza gastos, investimentos, patrimônio e renda passiva em um só lugar, com simplicidade e clareza.
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-[#004C27] to-[#006437] rounded-3xl p-8 shadow-2xl">
            <div className="bg-white rounded-2xl p-6">
              {/* Dashboard mockup consolidado */}
              <div className="grid md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-[#004C27] to-[#006437] rounded-xl p-4 text-white">
                  <TrendingUp className="w-8 h-8 mb-2 opacity-80" />
                  <p className="text-sm opacity-80">Total Investido</p>
                  <p className="text-2xl font-bold">R$ 56.970</p>
                </div>
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
                  <HomeIcon className="w-8 h-8 mb-2 opacity-80" />
                  <p className="text-sm opacity-80">Patrimônio Total</p>
                  <p className="text-2xl font-bold">R$ 124.500</p>
                </div>
                <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white">
                  <DollarSign className="w-8 h-8 mb-2 opacity-80" />
                  <p className="text-sm opacity-80">Dólares</p>
                  <p className="text-2xl font-bold">$ 2.850</p>
                </div>
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
                  <Coins className="w-8 h-8 mb-2 opacity-80" />
                  <p className="text-sm opacity-80">Renda Passiva</p>
                  <p className="text-2xl font-bold">R$ 890/mês</p>
                </div>
              </div>
              
              {/* Gráfico mockup */}
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex justify-between items-center mb-4">
                  <span className="font-semibold text-gray-700">Evolução Patrimonial</span>
                  <span className="text-sm text-green-600 font-medium">+12,5% este ano</span>
                </div>
                <div className="h-32 flex items-end justify-around gap-2">
                  {[35, 42, 38, 55, 48, 62, 58, 75, 68, 85, 78, 92].map((h, i) => (
                    <div key={i} className="flex-1 bg-gradient-to-t from-[#004C27] to-[#00994B] rounded-t transition-all duration-300 hover:opacity-80" style={{height: `${h}%`}}></div>
                  ))}
                </div>
                <div className="flex justify-between mt-2 text-xs text-gray-500">
                  {['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'].map(m => (
                    <span key={m}>{m}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAIXA 4 - INVESTIMENTOS */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Controle seus investimentos no Brasil e no exterior
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {investmentCards.map((card, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100">
                <div className="w-14 h-14 bg-[#004C27]/10 rounded-xl flex items-center justify-center mb-4">
                  <card.icon className="w-7 h-7 text-[#004C27]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{card.title}</h3>
                <p className="text-gray-600">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAIXA 5 - RESERVAS, PATRIMÔNIO E VIAGENS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Planeje, proteja e organize seu patrimônio
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Coins className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900">Reservas de Valor</h3>
                    <p className="text-gray-600">Ouro, Bitcoin, Ethereum e outras reservas</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Globe className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900">Planejamento de Viagens</h3>
                    <p className="text-gray-600">Organize suas finanças para viagens internacionais</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#004C27]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Building className="w-6 h-6 text-[#004C27]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900">Controle de Patrimônio</h3>
                    <p className="text-gray-600">Imóveis, veículos e outros bens</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-3xl p-8">
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h4 className="font-bold text-gray-900 mb-4">Reservas de Valor</h4>
                <div className="space-y-3">
                  {[
                    { name: 'Ouro', value: 'R$ 15.000', percent: '+8,2%' },
                    { name: 'Bitcoin', value: 'R$ 12.500', percent: '+45,3%' },
                    { name: 'Ethereum', value: 'R$ 5.200', percent: '+22,1%' }
                  ].map((item, i) => (
                    <div key={i} className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                      <span className="font-medium text-gray-700">{item.name}</span>
                      <div className="text-right">
                        <p className="font-bold text-gray-900">{item.value}</p>
                        <p className="text-sm text-green-600">{item.percent}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAIXA 6 - RENDA PASSIVA (DESTAQUE) */}
      <section className="py-24 bg-gradient-to-br from-[#004C27] to-[#006437] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="inline-block bg-amber-400 text-[#004C27] px-4 py-2 rounded-full font-bold text-sm mb-6">
                PRINCIPAL DIFERENCIAL
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Acompanhe sua renda passiva mês a mês
              </h2>
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                Registre seus proventos e saiba exatamente quanto sua carteira gera de renda passiva todos os meses.
              </p>
              <div className="flex items-center gap-4">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4">
                  <p className="text-sm text-white/80">Média Mensal</p>
                  <p className="text-3xl font-bold">R$ 890,00</p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4">
                  <p className="text-sm text-white/80">Total Anual</p>
                  <p className="text-3xl font-bold">R$ 10.680</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-3xl p-6 shadow-2xl">
              <h4 className="font-bold text-gray-900 mb-4">Proventos Mensais</h4>
              <div className="h-48 flex items-end justify-around gap-2 mb-4">
                {[650, 720, 680, 850, 790, 920, 880, 950, 890, 1020, 980, 1050].map((h, i) => (
                  <div key={i} className="flex-1 bg-gradient-to-t from-[#004C27] to-[#00994B] rounded-t relative group" style={{height: `${(h/1050)*100}%`}}>
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      R$ {h}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                {['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'].map(m => (
                  <span key={m}>{m}</span>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
                <span className="text-gray-600">Crescimento YoY</span>
                <span className="text-green-600 font-bold">+18,5%</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAIXA 7 - PARA QUEM É */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12">
            O AP – Finanças Pessoais é para você que:
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Quer construir renda passiva",
              "Quer investir com organização",
              "Quer planejar o futuro",
              "Quer controle financeiro real"
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-md flex items-center gap-4 text-left">
                <div className="w-12 h-12 bg-[#004C27] rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <span className="text-lg font-medium text-gray-800">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAIXA 8 - GARANTIA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-3xl p-8 md:p-12 text-center border-2 border-amber-200">
            <div className="w-20 h-20 bg-amber-400 rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              7 dias de garantia
            </h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Teste o AP – Finanças Pessoais por 7 dias. Se não fizer sentido, cancele sem burocracia.
            </p>
          </div>
        </div>
      </section>

      {/* FAIXA 9 - FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Perguntas Frequentes
          </h2>
          <div className="space-y-4">
            {faqItems.map((item, i) => (
              <div key={i} className="bg-white rounded-2xl shadow-md overflow-hidden">
                <button
                  onClick={() => toggleFaq(i)}
                  className="w-full px-6 py-5 flex justify-between items-center text-left"
                >
                  <span className="font-semibold text-gray-900">{item.question}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-600">{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAIXA 10 - CTA FINAL */}
      <section className="py-24 bg-gradient-to-br from-[#004C27] to-[#006437]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-8">
            Comece agora a organizar seus investimentos e sua renda passiva
          </h2>
          <button 
            onClick={handleAssinarAgora}
            className="inline-block px-12 py-5 bg-white text-[#004C27] rounded-xl font-bold text-xl hover:bg-gray-100 transition-all duration-300 hover:scale-105 shadow-2xl cursor-pointer"
            data-testid="cta-assinar-btn"
          >
            Assinar agora
          </button>
        </div>
      </section>

      {/* Footer - Mesmo da Home */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* Logo e Descrição */}
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_finance-dash-68/artifacts/7w39hvkz_Logo%20PP%20sem%20fundo.png" 
                alt="Pavan e Parazzi" 
                className="h-16 w-auto mb-4 brightness-0 invert"
              />
              <p className="text-gray-400 text-sm">
                Soluções em tecnologia e serviços integrados para impulsionar seu negócio e vida pessoal.
              </p>
            </div>

            {/* Links */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Links Úteis</h3>
              <ul className="space-y-2">
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    Termos de Uso
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    Política de Privacidade
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    Contato
                  </a>
                </li>
              </ul>
            </div>

            {/* Redes Sociais */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Conecte-se</h3>
              <div className="flex space-x-4 mb-4">
                <a 
                  href="https://www.instagram.com/pavaneparazzi" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-gray-800 p-3 rounded-full hover:bg-pink-600 transition-colors"
                >
                  <Instagram size={24} />
                </a>
                <a 
                  href="https://www.facebook.com/pavaneparazzi" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-gray-800 p-3 rounded-full hover:bg-blue-600 transition-colors"
                >
                  <Facebook size={24} />
                </a>
                <a 
                  href="https://wa.me/5519920008711" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-gray-800 p-3 rounded-full hover:bg-green-600 transition-colors"
                >
                  <Phone size={24} />
                </a>
              </div>
              <p className="text-gray-400 text-sm">
                <Phone size={16} className="inline mr-2" />
                (19) 9 2000-8711
              </p>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400 text-sm">
              © 2025 Pavan e Parazzi – Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingAP;
